/**
 * Support Request Clinician Detail Page - View and respond to support requests
 *
 * Mobile-friendly full page for clinicians to view request details,
 * respond, and resolve requests.
 */

import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { Router, type RouterLocation } from "@vaadin/router";
import { supportService } from "../services/support";
import { relationshipService } from "../services/relationships";
import { MonitoredUserAdapter } from "../services/history-data-adapter";
import type { SupportRequestDetail, SupportResponse } from "../types/support";
import {
  PRIORITY_INFO,
  STATUS_INFO,
  SupportRequestStatus,
} from "../types/support";
import "@awesome.me/webawesome/dist/components/button/button.js";
import "@awesome.me/webawesome/dist/components/icon/icon.js";
import "@awesome.me/webawesome/dist/components/spinner/spinner.js";
import "@awesome.me/webawesome/dist/components/callout/callout.js";
import "@awesome.me/webawesome/dist/components/badge/badge.js";
import "@awesome.me/webawesome/dist/components/textarea/textarea.js";
import "@awesome.me/webawesome/dist/components/tab-group/tab-group.js";
import "@awesome.me/webawesome/dist/components/tab/tab.js";
import "@awesome.me/webawesome/dist/components/tab-panel/tab-panel.js";
import "../components/app-shell";
import "./history-page";

@customElement("support-request-clinician-page")
export class SupportRequestClinicianPage extends LitElement {
  @property({ type: Object }) location?: RouterLocation;

  @state() private requestDetail: SupportRequestDetail | null = null;
  @state() private patientAdapter: MonitoredUserAdapter | null = null;
  @state() private isLoading = true;
  @state() private error = "";

  // Form state
  @state() private responseMessage = "";
  @state() private resolveNote = "";
  @state() private isSubmitting = false;

  static styles = css`
    :host {
      display: block;
    }

    .page-content {
      padding: 1rem;
      max-width: 900px;
      margin: 0 auto;
    }

    .back-header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 1rem;
      padding-bottom: 1rem;
      border-bottom: 1px solid #e9ecef;
    }

    .back-header h1 {
      margin: 0;
      font-size: 1.25rem;
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .request-card {
      background: #fff;
      border: 2px solid #e9ecef;
      border-radius: 0.75rem;
      padding: 1rem;
      margin-bottom: 1rem;
    }

    .request-card.priority-question {
      border-left: 4px solid #2196f3;
    }

    .request-card.priority-help-this-week {
      border-left: 4px solid #ff9800;
    }

    .request-card.priority-urgent {
      border-left: 4px solid #f44336;
    }

    .request-subject {
      font-size: 1.125rem;
      font-weight: 600;
      color: #333;
      margin: 0 0 0.5rem 0;
    }

    .request-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      margin-bottom: 0.75rem;
      font-size: 0.875rem;
    }

    .meta-item {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      color: #666;
    }

    .priority-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      padding: 0.25rem 0.5rem;
      border-radius: 0.25rem;
      font-size: 0.75rem;
      font-weight: 500;
    }

    .patient-info {
      background: #f8f9fa;
      border-radius: 0.5rem;
      padding: 0.75rem;
      margin-bottom: 0.75rem;
    }

    .patient-name {
      font-weight: 600;
      color: #333;
    }

    .patient-email {
      font-size: 0.875rem;
      color: #666;
    }

    .initial-message {
      background: #f8f9fa;
      border-radius: 0.5rem;
      padding: 1rem;
      white-space: pre-wrap;
      line-height: 1.5;
    }

    .section {
      margin-bottom: 1.5rem;
    }

    .section-label {
      font-size: 0.75rem;
      font-weight: 600;
      color: #6c757d;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      margin-bottom: 0.5rem;
    }

    .responses-list {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    .response-item {
      background: #e3f2fd;
      border-radius: 0.5rem;
      padding: 0.75rem;
    }

    .response-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.5rem;
      font-size: 0.8125rem;
    }

    .response-author {
      font-weight: 500;
      color: #1565c0;
    }

    .response-date {
      color: #666;
    }

    .response-message {
      white-space: pre-wrap;
      line-height: 1.5;
    }

    .action-card {
      background: #fff;
      border: 2px solid #e9ecef;
      border-radius: 0.75rem;
      padding: 1rem;
      margin-bottom: 1rem;
    }

    .action-card h3 {
      margin: 0 0 0.75rem 0;
      font-size: 1rem;
      color: #333;
    }

    .action-card wa-textarea {
      width: 100%;
      margin-bottom: 0.75rem;
    }

    .action-buttons {
      display: flex;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    .resolution-box {
      background: #e8f5e9;
      border: 1px solid #4caf50;
      border-radius: 0.5rem;
      padding: 1rem;
      margin-bottom: 1rem;
    }

    .resolution-header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-weight: 600;
      color: #2e7d32;
      margin-bottom: 0.5rem;
    }

    .resolution-meta {
      font-size: 0.8125rem;
      color: #666;
      margin-bottom: 0.5rem;
    }

    .resolution-note {
      white-space: pre-wrap;
      line-height: 1.5;
    }

    wa-tab-group {
      margin-top: 1rem;
    }

    wa-tab-panel {
      padding-top: 1rem;
    }

    .history-container {
      margin: -1rem;
    }

    history-page {
      --history-max-width: 100%;
    }

    .loading-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 3rem;
      text-align: center;
      color: #666;
    }

    .empty-responses {
      text-align: center;
      padding: 1.5rem;
      color: #666;
      background: #f8f9fa;
      border-radius: 0.5rem;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this._loadData();
  }

  updated(changedProperties: Map<string, unknown>) {
    if (changedProperties.has("location")) {
      this._loadData();
    }
  }

  private get requestId(): string {
    return (this.location?.params?.id as string) || "";
  }

  private async _loadData() {
    if (!this.requestId) {
      this.error = "No request ID provided";
      this.isLoading = false;
      return;
    }

    try {
      this.isLoading = true;
      this.error = "";

      // Load request detail and monitored users in parallel
      const [detail, monitoredUsers] = await Promise.all([
        supportService.getRequestDetail(this.requestId),
        relationshipService.getMonitoredUsers(),
      ]);

      this.requestDetail = detail;

      // Set up patient adapter for history view
      const patient = monitoredUsers.find(
        (u) => u.userId === detail.request.patientId,
      );
      if (patient) {
        this.patientAdapter = new MonitoredUserAdapter(
          patient.userId,
          patient.displayName,
          patient.email,
        );
      }
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to load request";
    } finally {
      this.isLoading = false;
    }
  }

  private async _sendResponse() {
    if (!this.requestDetail || !this.responseMessage.trim()) return;

    try {
      this.isSubmitting = true;
      this.error = "";

      await supportService.addResponse(this.requestDetail.request.requestId, {
        message: this.responseMessage.trim(),
      });

      this.responseMessage = "";
      await this._loadData();
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to send response";
    } finally {
      this.isSubmitting = false;
    }
  }

  private async _resolveRequest() {
    if (!this.requestDetail) return;

    try {
      this.isSubmitting = true;
      this.error = "";

      await supportService.resolveRequest(
        this.requestDetail.request.requestId,
        this.resolveNote.trim()
          ? { resolutionNote: this.resolveNote.trim() }
          : undefined,
      );

      this.resolveNote = "";
      await this._loadData();
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to resolve request";
    } finally {
      this.isSubmitting = false;
    }
  }

  private async _assignToMe() {
    if (!this.requestDetail) return;

    try {
      this.isSubmitting = true;
      this.error = "";

      await supportService.assignRequest(this.requestDetail.request.requestId);

      await this._loadData();
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to assign request";
    } finally {
      this.isSubmitting = false;
    }
  }

  private _goBack() {
    Router.go("/support-dashboard");
  }

  private _formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-NZ", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  render() {
    return html`
      <app-shell pageTitle="Request Details">
        <div class="page-content">
          <div class="back-header">
            <wa-button variant="text" size="small" @click=${this._goBack}>
              <wa-icon name="arrow-left"></wa-icon>
            </wa-button>
            <h1>Support Request</h1>
          </div>

          ${this.error
            ? html`
                <wa-callout variant="danger" open style="margin-bottom: 1rem;">
                  <wa-icon slot="icon" name="octagon-exclamation"></wa-icon>
                  ${this.error}
                </wa-callout>
              `
            : null}
          ${this.isLoading
            ? html`
                <div class="loading-container">
                  <wa-spinner style="font-size: 2rem;"></wa-spinner>
                  <p>Loading...</p>
                </div>
              `
            : this.requestDetail
              ? this._renderContent()
              : null}
        </div>
      </app-shell>
    `;
  }

  private _renderContent() {
    const { request, responses } = this.requestDetail!;

    return html`
      <wa-tab-group>
        <wa-tab slot="nav" panel="request">Request</wa-tab>
        <wa-tab slot="nav" panel="history">Patient History</wa-tab>

        <wa-tab-panel name="request">
          ${this._renderRequestTab(request, responses)}
        </wa-tab-panel>

        <wa-tab-panel name="history">
          ${this._renderHistoryTab()}
        </wa-tab-panel>
      </wa-tab-group>
    `;
  }

  private _renderRequestTab(
    request: SupportRequestDetail["request"],
    responses: SupportResponse[],
  ) {
    const priorityInfo = PRIORITY_INFO[request.priority];
    const statusInfo = STATUS_INFO[request.status];
    const isResolved = request.status === SupportRequestStatus.Resolved;

    return html`
      <div class="request-card priority-${request.priority}">
        <h2 class="request-subject">${request.subject}</h2>

        <div class="request-meta">
          <span
            class="priority-badge"
            style="background: ${priorityInfo.color}22; color: ${priorityInfo.color}"
          >
            ${priorityInfo.label}
          </span>
          <wa-badge variant="${isResolved ? "success" : "neutral"}">
            ${statusInfo.label}
          </wa-badge>
          <span class="meta-item">
            <wa-icon name="calendar-days"></wa-icon>
            ${this._formatDate(request.createdAt)}
          </span>
        </div>

        <div class="patient-info">
          <div class="patient-name">${request.patientName}</div>
          ${request.patientEmail
            ? html`<div class="patient-email">${request.patientEmail}</div>`
            : null}
        </div>

        <div class="section-label">Patient's Message</div>
        <div class="initial-message">${request.initialNote}</div>
      </div>

      ${isResolved && request.resolutionNote
        ? html`
            <div class="resolution-box">
              <div class="resolution-header">
                <wa-icon name="circle-check"></wa-icon>
                Resolved
              </div>
              <div class="resolution-meta">
                By ${request.resolvedByName} on
                ${this._formatDate(request.resolvedAt!)}
              </div>
              <div class="resolution-note">${request.resolutionNote}</div>
            </div>
          `
        : null}

      <div class="section">
        <div class="section-label">Responses (${responses.length})</div>
        ${responses.length > 0
          ? html`
              <div class="responses-list">
                ${responses.map((r) => this._renderResponse(r))}
              </div>
            `
          : html` <div class="empty-responses">No responses yet</div> `}
      </div>

      ${!isResolved ? this._renderActions() : null}
    `;
  }

  private _renderResponse(response: SupportResponse) {
    return html`
      <div class="response-item">
        <div class="response-header">
          <span class="response-author">${response.clinicianName}</span>
          <span class="response-date"
            >${this._formatDate(response.createdAt)}</span
          >
        </div>
        <div class="response-message">${response.message}</div>
      </div>
    `;
  }

  private _renderActions() {
    const request = this.requestDetail!.request;

    return html`
      <div class="action-card">
        <h3>Send Response</h3>
        <wa-textarea
          placeholder="Type your response to the patient..."
          rows="3"
          .value=${this.responseMessage}
          @input=${(e: CustomEvent) =>
            (this.responseMessage = (e.target as HTMLInputElement).value)}
        ></wa-textarea>
        <div class="action-buttons">
          <wa-button
            variant="brand"
            @click=${this._sendResponse}
            ?loading=${this.isSubmitting}
            ?disabled=${!this.responseMessage.trim()}
          >
            <wa-icon slot="prefix" name="send"></wa-icon>
            Send
          </wa-button>
          ${request.status === SupportRequestStatus.Open
            ? html`
                <wa-button
                  variant="neutral"
                  @click=${this._assignToMe}
                  ?loading=${this.isSubmitting}
                >
                  <wa-icon slot="prefix" name="person-check"></wa-icon>
                  Assign to Me
                </wa-button>
              `
            : null}
        </div>
      </div>

      <div class="action-card">
        <h3>Resolve Request</h3>
        <wa-textarea
          placeholder="Add a resolution note (optional)..."
          rows="2"
          .value=${this.resolveNote}
          @input=${(e: CustomEvent) =>
            (this.resolveNote = (e.target as HTMLInputElement).value)}
        ></wa-textarea>
        <wa-button
          variant="success"
          @click=${this._resolveRequest}
          ?loading=${this.isSubmitting}
        >
          <wa-icon slot="prefix" name="circle-check"></wa-icon>
          Mark as Resolved
        </wa-button>
      </div>
    `;
  }

  private _renderHistoryTab() {
    if (!this.patientAdapter) {
      return html`
        <div class="empty-responses">
          Patient history not available. This patient may not be in your
          monitored users list.
        </div>
      `;
    }

    return html`
      <div class="history-container">
        <history-page .dataAdapter=${this.patientAdapter}></history-page>
      </div>
    `;
  }
}
